import { useEffect, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { ifLinks, mmLinks } from 'constants/links';
import { FeesModal } from 'features/account/orders/FeesModal';
import FrozenPackagingModal from 'features/shop/cart/FrozenPackagingModal';
import { Alert } from 'honeydew/Alert';
import { Divider } from 'honeydew/Divider';
import SavingsIcon from 'honeydew/icons/Orders/SavingsIcon';
import StarIcon from 'honeydew/icons/Orders/StarIcon';
import useGlobalStore from 'hooks/useGlobalStore';
import DriverTipsInfoModal from 'shared/driver-tips/DriverTipsInfoModal';

const gridItemStyles = 'text-body-md grid grid-cols-2 justify-between';

function OrderSummary({ featureCanAddDriverTips, isImperfectLegacy, order }) {
    const { isMisfitsTheme } = useGlobalStore();

    const { data: subscription = {} } = useSubscriptionQuery();
    const { last4 } = subscription?.billing ?? {};

    const [coldKeeperItems, setColdKeeperItems] = useState(false);
    const [driverTipsModalOpen, setDriverTipsModalOpen] = useState(false);
    const [feesModalOpen, setFeesModalOpen] = useState(false);
    const [frozenFeesModalOpen, setFrozenFeesModalOpen] = useState(false);

    const { creditsForLoyaltyRefunds, tipAmountRefunded, totalRefund, totalStoreCreditFromAutomatedRefunds } =
        order.refundHistory ?? {};

    const { basebox, coldKeeperFee: coldKeeperFeeFromCharge } = order.charge ?? {};

    const {
        alcoholDiscountAmount,
        alcoholDiscountPercentage,
        alcoholFeeWaived,
        alcoholServiceFee,
        coldKeeperFee,
        coldKeeperFeeWaived,
        creditCardAmountRefunded,
        credits,
        creditsRefunded,
        discountAmount,
        discountCode,
        driverTip,
        ebtSnapRefunded,
        ebtSnapUsed,
        frozenFee,
        frozenFeeWaived,
        loyaltyPointsDiscount,
        loyaltyPointsEarned,
        loyaltyPointsRefunded,
        nonSnapEligibleTotal,
        operationsFee,
        refundToOriginalPaymentMethod,
        shippingFee,
        shippingFeeWaived,
        snapEligibleTotal,
        subtotal,
        total,
        totalSavings,
        totalTaxes,
    } = order.receipt ?? {};

    const canceledOrder = order?.id
        ? (order.shipments.length > 0 && order.shipments[0].shippingStatus === 'CANCELLED') ||
          (order.drinksShipments.length > 0 && order.drinksShipments[0].shippingStatus === 'CANCELLED')
        : false;

    // NOTE: We may need to add EBT refund amount here if totalRefund does not include EBT refunds.
    const refundTotal = parseFloat(totalRefund) + totalStoreCreditFromAutomatedRefunds + creditsForLoyaltyRefunds;

    const displayRefundModule =
        !canceledOrder &&
        (parseFloat(refundToOriginalPaymentMethod) > 0 || parseFloat(creditsRefunded) > 0 || loyaltyPointsRefunded > 0);

    const hasDiscountsApplied = discountAmount > 0 && discountCode && discountCode !== '';
    const isDeliveredByImperfect = order.shipments[0]?.carrier === 'dbi';

    const displayPaymentSplit = ebtSnapUsed > 0 && total > 0;
    const displayItemEligibilitySplit = ebtSnapUsed > 0;

    useEffect(() => {
        if (order?.shipments?.length) {
            const items = order.shipments.flatMap((shipment) => shipment.items);
            setColdKeeperItems(items.some((item) => item?.coldPacked));
        }
    }, [order?.shipments]);

    const showAlcoholServiceFee = order.hasDrinks;

    const showColdPackFee = coldKeeperItems && !isImperfectLegacy && !isDeliveredByImperfect;

    const showOperationsFee = operationsFee > 0;

    // NOTE:
    // - order.receipt.total does not include EBT payments, so we're adding on the FE to get a true total on EBT orders.
    // - total is returned as a string, while ebtSnapUsed is a number
    const grandTotal =
        ebtSnapUsed > 0 ? (parseFloat(total) + (ebtSnapUsed ?? 0)).toFixed(2) : parseFloat(total).toFixed(2);

    return (
        <div id="orderDetails">
            <div className="grid gap-10">
                {displayItemEligibilitySplit && (
                    <>
                        <div className={gridItemStyles} data-testid="order-summary-snap-eligible-items-total">
                            <p>SNAP-Eligible Items</p>
                            <p className="text-right">${snapEligibleTotal?.toFixed(2)}</p>
                        </div>

                        <div className={gridItemStyles} data-testid="order-summary-non-snap-eligible-items">
                            <p>Non-SNAP-Eligible Items</p>
                            <p className="text-right">${nonSnapEligibleTotal?.toFixed(2)}</p>
                        </div>
                    </>
                )}
                {/* Check out https://github.com/MisfitsMarket/boysenberry/pull/1115#discussion_r2525019514 for more
                   details about productID 6. TODO: Clean up this logic when we can. */}
                {!displayItemEligibilitySplit && order.productID === 6 && (
                    <div className={gridItemStyles}>
                        <p>Subtotal</p>
                        <p className="text-right" id="subtotal">
                            ${subtotal}
                        </p>
                    </div>
                )}
                {!displayItemEligibilitySplit && order.productID !== 6 && (
                    <>
                        <div className={gridItemStyles}>
                            <p>Base box</p>
                            <p>${basebox?.toFixed(2)}</p>
                        </div>

                        {subtotal > 0 && (
                            <div className={gridItemStyles}>
                                <p>Marketplace subtotal</p>
                                <p className="text-right" id="subtotal">
                                    ${subtotal}
                                </p>
                            </div>
                        )}
                    </>
                )}
                {order.shipments.length > 0 && (
                    <div className={gridItemStyles}>
                        <p>Shipping</p>
                        <p className="text-right" id="shipping">
                            {shippingFee > 0 && !shippingFeeWaived ? (
                                `$${shippingFee?.toFixed(2)}`
                            ) : (
                                <span className="text-brand-primary font-grotesk-bold">FREE</span>
                            )}
                        </p>
                    </div>
                )}
                {showOperationsFee && (
                    <div className={gridItemStyles} data-testid="operations-fee">
                        <div className="flex items-center gap-5">
                            <p>Operations Fee</p>
                            <button
                                aria-label="Operations fee information"
                                onClick={() => setFeesModalOpen(true)}
                                type="button"
                            >
                                <InformationCircleIcon className="mb-[2px]" width={18} />
                            </button>
                        </div>
                        <p className="text-right">${operationsFee.toFixed(2)}</p>
                    </div>
                )}
                {(frozenFee > 0 || frozenFeeWaived) && (
                    <div className={gridItemStyles} data-testid="frozen-fee">
                        <div className="flex items-center gap-5">
                            <p>Frozen Packaging</p>
                            <button
                                aria-label="Frozen Packaging fee information"
                                onClick={() => setFrozenFeesModalOpen(true)}
                                type="button"
                            >
                                <InformationCircleIcon className="mb-[2px]" width={18} />
                            </button>
                        </div>
                        <p className="text-right">
                            {frozenFeeWaived ? (
                                <span className="text-brand-primary font-grotesk-bold">WAIVED</span>
                            ) : (
                                `$${frozenFee.toFixed(2)}`
                            )}
                        </p>
                        <FrozenPackagingModal
                            open={frozenFeesModalOpen}
                            handleClose={() => setFrozenFeesModalOpen(false)}
                            frozenFee={frozenFee}
                        />
                    </div>
                )}
                {showColdPackFee && (
                    <div className={gridItemStyles} data-testid="cold-pack-fee">
                        <div className="flex items-center gap-5">
                            <p>Cold Pack Fee</p>
                            <button
                                aria-label="Cold pack fee information"
                                onClick={() => setFeesModalOpen(true)}
                                type="button"
                            >
                                <InformationCircleIcon className="mb-[2px]" width={18} />
                            </button>
                        </div>
                        <p className="text-right">
                            {coldKeeperFeeWaived ? (
                                <span className="text-brand-primary font-grotesk-bold">WAIVED</span>
                            ) : (
                                `$${(coldKeeperFee || coldKeeperFeeFromCharge)?.toFixed(2)}`
                            )}
                        </p>
                    </div>
                )}
                {showAlcoholServiceFee && (
                    <div className={gridItemStyles} data-testid="alcohol-service-fee">
                        <div className="flex items-center gap-5">
                            <p>Alcohol Service Fee</p>
                            <button
                                aria-label="Alcohol service fee information"
                                onClick={() => setFeesModalOpen(true)}
                                type="button"
                            >
                                <InformationCircleIcon className="mb-[2px]" width={18} />
                            </button>
                        </div>
                        <p className="text-right" id="alcoholServiceFee">
                            {alcoholFeeWaived ? (
                                <span className="text-brand-primary font-grotesk-bold">WAIVED</span>
                            ) : (
                                `$${alcoholServiceFee?.toFixed(2)}`
                            )}
                        </p>
                    </div>
                )}
                {featureCanAddDriverTips && (
                    <div className={gridItemStyles}>
                        <div className="flex gap-5">
                            <p>{tipAmountRefunded > 0 ? 'Updated ' : ''}Driver Tip</p>
                            <button type="button" onClick={() => setDriverTipsModalOpen(!driverTipsModalOpen)}>
                                <InformationCircleIcon className="-mt-[4px] w-[18px] text-black" />
                            </button>
                        </div>
                        <p className="text-right" id="driverTip">
                            ${(driverTip || 0).toFixed(2)}
                        </p>
                    </div>
                )}
                {hasDiscountsApplied && (
                    <div className={gridItemStyles}>
                        <p>
                            Discount <span className="text-brand-primary">({discountCode})</span>
                        </p>
                        <p className="text-right">-${discountAmount?.toFixed(2)}</p>
                    </div>
                )}
                {/* Legacy: This section should only appear for historical orders */}
                {loyaltyPointsDiscount > 0 && (
                    <div className={gridItemStyles}>
                        <p>Misfits Perks</p>
                        <p className="text-right">- ${loyaltyPointsDiscount?.toFixed(2)}</p>
                    </div>
                )}
                {alcoholDiscountAmount > 0 && (
                    <div className={gridItemStyles}>
                        <p className="flex">Alcohol Savings (-{alcoholDiscountPercentage}%)</p>
                        <p className="text-right">-${alcoholDiscountAmount.toFixed(2)}</p>
                    </div>
                )}
                {totalTaxes != null && (
                    <div className={gridItemStyles} data-testid="tax">
                        <p>Tax</p>
                        <p className="text-right" id="tax">
                            ${totalTaxes}
                        </p>
                    </div>
                )}
                {credits > 0 && (
                    <div className={clsx(gridItemStyles, 'text-brand-primary')}>
                        <p>Credits</p>
                        <p className="text-right" id="credits">
                            - ${credits?.toFixed(2)}
                        </p>
                    </div>
                )}
            </div>

            <Divider />

            <div className="text-body-md grid grid-cols-2 justify-between" data-testid="order-summary-total">
                <p className="font-grotesk-bold">Total {canceledOrder && refundTotal > 0 && 'Charged'}</p>
                <p className="text-right font-grotesk-bold" id="total">
                    ${grandTotal}
                </p>
            </div>

            {displayPaymentSplit && (
                <div data-testid="order-summary-payment-split" className="flex flex-col gap-10">
                    <p className="mt-20 font-grotesk-bold">Your Payment Split</p>
                    {ebtSnapUsed > 0 && (
                        <div className={gridItemStyles}>
                            <p>EBT Payment</p>
                            <p className="text-right">${ebtSnapUsed.toFixed(2)}</p>
                        </div>
                    )}
                    {ebtSnapRefunded > 0 && (
                        <div className="text-body-md grid grid-cols-2 justify-between bg-malt p-5 if:bg-off-white">
                            <p>Refunded to EBT Card</p>
                            <p className="text-right text-error">${ebtSnapRefunded.toFixed(2)}</p>
                        </div>
                    )}
                    {total > 0 && (
                        <div className={gridItemStyles}>
                            {/* NOTE: this is the last4 of your card on file, not necessarily the last4 of the card used for the order */}
                            <p>Credit Card{last4 ? ` *${last4}` : ''} Payment</p>
                            <p className="text-right">${parseFloat(total).toFixed(2)}</p>
                        </div>
                    )}
                    {creditCardAmountRefunded > 0 && (
                        <div className="text-body-md grid grid-cols-2 justify-between bg-malt p-5 if:bg-off-white">
                            <p>Refunded to Credit Card</p>
                            <p className="text-right text-error">${creditCardAmountRefunded.toFixed(2)}</p>
                        </div>
                    )}
                </div>
            )}

            {displayRefundModule && (
                <div className="bg-malt-off-white my-15 rounded-md p-10">
                    {parseFloat(creditsRefunded) > 0 && (
                        <div className="flex justify-between">
                            <p>Refunded to credit balance</p>
                            <p className="text-right text-error">${creditsRefunded}</p>
                        </div>
                    )}

                    {parseFloat(refundToOriginalPaymentMethod) > 0 && (
                        <div className="flex justify-between">
                            <p>Refunded to original payment method</p>
                            <p data-testid="cc-refund" className="text-right text-error">
                                ${refundToOriginalPaymentMethod}
                            </p>
                        </div>
                    )}

                    {loyaltyPointsRefunded > 0 && (
                        <div className="flex justify-between" data-testid="loyalty-points-refund">
                            <p>Refunded to Misfits Perks</p>
                            <p className="text-right text-error">{loyaltyPointsRefunded} pts</p>
                        </div>
                    )}
                </div>
            )}

            {isImperfectLegacy ? (
                <div className="mt-20">
                    <Alert>
                        For detailed pricing, look for your order confirmation email (&quot;Your order is
                        confirmed!&quot;) and reach out to{' '}
                        <a
                            className="link"
                            href={isMisfitsTheme ? mmLinks.customerCare : ifLinks.customerCare}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Customer Care
                        </a>{' '}
                        if you have any questions. All future orders will include itemized prices per usual.
                    </Alert>
                </div>
            ) : (
                <>
                    <Divider />

                    <div className="mx-auto flex flex-col items-center justify-center gap-10 md:w-[400px] md:flex-row md:gap-40">
                        {totalSavings > 0 && refundTotal === 0 && (
                            <div className="text-body-xs flex items-center gap-10">
                                <SavingsIcon isMisfits={isMisfitsTheme} />
                                <p className="font-grotesk-bold">
                                    You&apos;re saving{' '}
                                    <span className="text-brand-primary">${parseFloat(totalSavings).toFixed(2)}</span>
                                </p>
                            </div>
                        )}

                        {loyaltyPointsEarned > 0 && (
                            <div className="text-body-xs flex items-center gap-10">
                                <StarIcon isMisfits={isMisfitsTheme} />
                                <p className="font-grotesk-bold" id="loyaltyPointsTxt">
                                    You&apos;re earning{' '}
                                    <span className="text-brand-primary">{loyaltyPointsEarned} Points</span>
                                </p>
                            </div>
                        )}
                    </div>
                </>
            )}

            <DriverTipsInfoModal open={driverTipsModalOpen} setOpen={setDriverTipsModalOpen} />
            <FeesModal
                alcoholServiceFee={{
                    display: showAlcoholServiceFee,
                    fee: alcoholServiceFee,
                    waived: alcoholFeeWaived,
                }}
                coldPackFee={{
                    display: showColdPackFee,
                    fee: coldKeeperFee || coldKeeperFeeFromCharge,
                    proteinMinimum: undefined,
                    waived: coldKeeperFeeWaived,
                }}
                handleClose={() => setFeesModalOpen(false)}
                open={feesModalOpen}
                operationsFee={{
                    display: showOperationsFee,
                    fee: operationsFee,
                }}
            />
        </div>
    );
}

OrderSummary.propTypes = {
    featureCanAddDriverTips: PropTypes.bool.isRequired,
    isImperfectLegacy: PropTypes.bool.isRequired,
    order: PropTypes.shape({}).isRequired,
};

export default OrderSummary;
